import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import type { ControllerRenderProps, FieldValues } from "react-hook-form";
import { useModuleBEM } from "@b2bportal/core-themes";
import { parseDayMonthAndYear } from "@hopper-b2b/utilities";
import clsx from "clsx";
import { calculateCursorPosition } from "../../../../utilities/form/calculateCursorPosition";
import styles from "./styles.module.scss";

export interface DateInputProps
  extends Omit<ControllerRenderProps<FieldValues, string>, "ref">,
    React.AriaAttributes {
  id?: string;
  placeholder?: string;
  className?: string;
  dateFormatter?: (date: string) => string;
}

const formatInputDate = (date: string): string => {
  const { month, day, year, strLength } = parseDayMonthAndYear(date);
  return (
    day + (strLength > 2 ? "/" : "") + month + (strLength > 4 ? "/" : "") + year
  );
};

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (props, formRef) => {
    const {
      className,
      placeholder,
      onChange,
      dateFormatter = formatInputDate,
      ...field
    } = props;
    const [display, setDisplay] = useState(dateFormatter(field.value || ""));
    const inputRef = useRef<HTMLInputElement>(null);
    const cursorPos = useRef<number>(0);

    const mergedRef = useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof formRef === "function") formRef(el);
        else if (formRef) formRef.current = el;
      },
      [formRef]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const cursorInRaw = e.target.selectionStart ?? raw.length;
      const formatted = dateFormatter(raw);

      cursorPos.current = calculateCursorPosition(raw, cursorInRaw, formatted);
      setDisplay(formatted);
      onChange(formatted);
    };

    useEffect(() => {
      if (inputRef.current == null) return;
      if (document.activeElement === inputRef.current) {
        inputRef.current.setSelectionRange(
          cursorPos.current,
          cursorPos.current
        );
      }
    }, [display]);

    const [, cn] = useModuleBEM(styles, "DateField");

    return (
      <input
        {...field}
        ref={mergedRef}
        placeholder={placeholder}
        inputMode="numeric"
        className={clsx(cn("input"), className)}
        onChange={handleChange}
        value={display}
      />
    );
  }
);

DateInput.displayName = "DateInput";
